import React from "react";
import { Input } from "@/components/ui/input";

/** Above this many files the operator must type the count to confirm, rather than just clicking through. */
export const TYPED_CONFIRMATION_THRESHOLD = 1000;

/** Whether confirming a change to `fileCount` files needs the count typed. */
export const needsTypedConfirmation = (fileCount: number): boolean => fileCount > TYPED_CONFIRMATION_THRESHOLD;

/** Whether `typed` is `fileCount`, ignoring the thousands separators and spaces people type. */
export const typedCountMatches = (typed: string, fileCount: number): boolean =>
  typed.replace(/[,\s]/g, "") === String(fileCount);

interface TypedCountConfirmationProps {
  fileCount: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * The "type the count to confirm" box shown before a change to more than {@link TYPED_CONFIRMATION_THRESHOLD} files.
 * Render it only when {@link needsTypedConfirmation} is true, and keep the confirm button disabled until
 * {@link typedCountMatches}.
 */
const TypedCountConfirmation: React.FC<TypedCountConfirmationProps> = ({ fileCount, value, onChange, disabled }) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs text-muted-foreground">
      Type <span className="font-mono font-medium">{fileCount}</span> to confirm.
    </span>
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={String(fileCount)}
      aria-label="File count to confirm"
      className="h-8 w-40 font-mono"
      disabled={disabled}
    />
  </div>
);

export default TypedCountConfirmation;
